// @ts-check
/**
 * Built map shapes kept on the phone (Stage 9, 2026-10-21: "pre-compute things ... make this feel instantaneous").
 *
 * The slow part of building a map is draping its surfaces on the ground - the Bowl's seating deck, its lots, every painted
 * walkway and street. The results depend only on the map and the app's code, so after a map is built once they are kept
 * here, per map version and app build, and the next build reads them instead of computing them again
 * (priiScene's drapedGeometry / drapeTris look them up by feature and call order).
 *
 * One record per map: { key, entries: [[callKey, value]...], at }. A handful of maps are kept; the oldest go first.
 * Dev builds are always 'dev' while the code changes under them, so the cache is off there unless ?geocache=1.
 */
import { mapStore } from '../map/mapStore';
import { whenQuiet } from '../quietMoment';
import { isPrefetchAllowed } from '../network/effectiveConnectivity';
import { appLog } from '../appLog';
import { decodeGeo } from './geoBlob';

/** The map scene code's fingerprint (scripts/map3d-hash.mjs, injected by vite.config): the kept shapes depend on the map
 *  and on this code, not on the app build - an update that leaves the map code alone keeps every map warm. */
export const MAP3D_HASH = import.meta.env.VITE_MAP3D_HASH || 'dev';

const DB = 'prii-geo';
const STORE = 'scenes';
const INDEX = 'index'; // { key, at } per map: trimming reads this, never the ~14 MB rows
const KEEP = 3; // the Bowl's shapes are ~14 MB; a few maps at most

let dbPromise = null;
function openDb() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(DB, 2);
        req.onupgradeneeded = () => {
          const db = req.result;
          // v1 kept no index; its rows are dropped rather than read (reading them is the cost this version removes)
          if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE);
          db.createObjectStore(STORE, { keyPath: 'key' });
          if (!db.objectStoreNames.contains(INDEX)) db.createObjectStore(INDEX, { keyPath: 'key' });
        };
        req.onsuccess = () => {
          const db = req.result;
          db.onversionchange = () => {
            db.close();
            dbPromise = null;
          };
          resolve(db);
        };
        req.onerror = () => {
          dbPromise = null;
          resolve(null);
        };
        req.onblocked = () => {
          dbPromise = null;
          resolve(null);
        };
      } catch {
        dbPromise = null;
        resolve(null);
      }
    });
  }
  return dbPromise;
}

function enabled() {
  if (!import.meta.env.DEV) return true;
  try {
    return new URLSearchParams(window.location.search).get('geocache') === '1';
  } catch {
    return false;
  }
}

/** The cache key for a map, or null when it cannot be told apart from another version of itself. */
export function geoKeyFor(eventId, payload) {
  if (!enabled()) return null;
  const edition = payload?.properties?.edition_id ?? null;
  const version = eventId ? mapStore.version(eventId) : null;
  if (!edition || version == null) return null;
  return `${MAP3D_HASH}|${edition}|${version}`;
}
/** The same key from a version stamp alone (`<edition>:<stamp>`, get_festival_map_version). */
export function geoKeyForVersion(version) {
  if (!enabled() || !version) return null;
  const edition = String(version).split(':')[0];
  return edition ? `${MAP3D_HASH}|${edition}|${version}` : null;
}
/** Where a map version's shipped shapes live (scripts/render_map_shapes.mjs puts them there; migration 168). */
export function geoBlobUrl(version) {
  const edition = String(version).split(':')[0];
  const base =
    import.meta.env.VITE_SHAPES_BASE ||
    `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/map-shapes`;
  return `${base}/${edition}/${MAP3D_HASH}/${String(version).replace(/[^A-Za-z0-9_-]/g, '-')}.bin`;
}

/** The kept shapes for a key as a Map, or null. Never throws; gives up after `timeoutMs` (the build goes on without). */
export async function loadGeo(key, timeoutMs = 300) {
  if (!key) return null;
  const read = (async () => {
    const db = await openDb();
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).get(key);
        req.onsuccess = () => resolve(req.result?.entries ? new Map(req.result.entries) : null);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  })();
  return Promise.race([read, new Promise((r) => setTimeout(() => r(null), timeoutMs))]);
}

/**
 * Keep a map's shapes, and drop all but the newest KEEP maps. Trimming reads the small index, never the rows:
 * a getAll over the rows deserialized every kept map (~14 MB each) on the main thread, and the phone froze for 5-45 s
 * after leaving the Bowl (2026-10-21 logs, perf.main_thread_stall right after each save).
 *
 * WHEN it is written (2026-10-31): Sam's phone logged `hits: 0, misses: 28, saved: true` on every Bowl open of one build -
 * the shapes were never read back because they were never written. The write waited for 4 s untouched plus 1.5 s on
 * screen and never ran while hidden, and a map open is tapped, looked at and put away in under 10 s; the web view does not
 * survive the background (app.launch on every return), so the pending write died with it. The row is 23 entries / 16.5 MB
 * (not the 10,600-entry 40 MB that made a save cost a second): its structured clone is ~25 ms on a laptop. So it is
 * written soon after the build (1.5 s untouched, or 4 s at most), and at once when the app leaves the screen, since a
 * suspended timer never fires again. `onSaved` says which path ran and what the put cost, for the phone's logs.
 */
export function saveGeo(key, entries, onSaved = null) {
  if (!key || !entries?.size) return;
  const rows = [...entries];
  const t0 = Date.now();
  let cancel = null,
    written = false;
  const onHide = () => {
    if (document.hidden) void write('hide');
  };
  const write = async (via) => {
    if (written) return;
    written = true;
    document.removeEventListener('visibilitychange', onHide);
    cancel?.();
    const info = { via, waitedMs: Date.now() - t0, putMs: null, ok: false };
    const db = await openDb();
    if (!db) {
      onSaved?.(info);
      return;
    }
    try {
      const tx = db.transaction([STORE, INDEX], 'readwrite');
      const st = tx.objectStore(STORE);
      const ix = tx.objectStore(INDEX);
      const at = Date.now();
      const tp = performance.now();
      st.put({ key, entries: rows, at });
      ix.put({ key, at });
      info.putMs = Math.round(performance.now() - tp); // the structured clone, the part that holds the main thread
      const all = ix.getAll();
      all.onsuccess = () => {
        const old = (all.result || [])
          .filter((r) => r.key !== key)
          .sort((a, b) => b.at - a.at)
          .slice(KEEP - 1);
        for (const r of old) {
          st.delete(r.key);
          ix.delete(r.key);
        }
      };
      tx.oncomplete = () => onSaved?.({ ...info, ok: true });
      tx.onerror = () => onSaved?.(info);
      tx.onabort = () => onSaved?.(info);
    } catch {
      onSaved?.(info); /* a cache that cannot be written is a cache that is not used */
    }
  };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onHide);
  cancel = whenQuiet(() => void write(Date.now() - t0 >= 4000 ? 'maxWait' : 'quiet'), {
    quietMs: 1500,
    afterMs: 500,
    maxWaitMs: 4000,
  });
}

/**
 * SHIPPED SHAPES (2026-10-31): the first open of a venue, show day for most people, was still a cold ~1.5 s build. The
 * published maps' kept shapes are rendered once, headless (scripts/render_map_shapes.mjs), and kept in the public
 * map-shapes bucket per edition, code hash and map version. Once a map is downloaded or found current, its blob is
 * fetched (~20 MB for the Bowl: only where prefetching is allowed) and written as the cache row, so the first open is
 * the warm one. A missing blob (not rendered yet, or the map code changed) is just a cold build, as before. Once per
 * key per app run; never over a row the phone already has.
 */
const seeded = new Set();
export async function seedGeoShapes(version) {
  const key = geoKeyForVersion(version);
  if (!key || seeded.has(key)) return false;
  seeded.add(key);
  try {
    if (await loadGeo(key, 2000)) return true; // built here already
    if (!isPrefetchAllowed()) {
      seeded.delete(key); // another day, on Wi-Fi
      return false;
    }
    const t0 = performance.now();
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 90_000);
    const res = await fetch(geoBlobUrl(version), { signal: ctl.signal }).finally(() =>
      clearTimeout(timer),
    );
    if (!res.ok) {
      appLog('info', 'map.geo_seed', { ok: false, status: res.status, version });
      return false;
    }
    const buf = await res.arrayBuffer();
    const entries = decodeGeo(buf);
    if (await loadGeo(key, 2000)) return true; // a build here beat the download to it
    saveGeo(key, entries, (w) =>
      appLog(w.ok ? 'info' : 'warn', 'map.geo_seed', {
        ok: w.ok,
        version,
        bytes: buf.byteLength,
        entries: entries.size,
        fetchMs: Math.round(performance.now() - t0),
        ...w,
      }),
    );
    return true;
  } catch (e) {
    appLog('info', 'map.geo_seed', {
      ok: false,
      version,
      message: String(e?.message ?? e).slice(0, 120),
    });
    return false;
  }
}

/** The object priiScene uses during a build: lookups from `hits`, new results collected for saving. */
export function geoSession(key, hits, loadMs = null) {
  let fresh = new Map();
  let misses = 0;
  const hitsCount = hits?.size ?? 0;
  return {
    key,
    get: (k) => hits?.get(k),
    put: (k, v) => {
      misses++;
      fresh?.set(k, v);
    },
    entries: () => {
      const all = new Map(hits || []);
      for (const [k, v] of fresh || []) all.set(k, v);
      return all;
    },
    get misses() {
      return misses;
    },
    hitsCount,
    loadMs,
    /* Worth writing back: a first build, or one that made a real share of its shapes again. A warm build always misses
       a few (2026-10-21 phone logs: 69 hits, 3 misses) and rewriting ~14 MB for those on every open is pure memory churn. */
    get worthSaving() {
      return misses > 0 && (hitsCount === 0 || misses > hitsCount * 0.1);
    },
    /* Drop both maps once the build is done; the scene keeps only the geometry it made. */
    release() {
      hits = null;
      fresh = null;
      this.spent = true;
    },
    spent: false,
  };
}

/** The `geo` priiScene takes: lookups and new results from the session, then save if worth it (`onSaved` when the write
 *  lands, or fails), log, and let go. */
export function geoForBuild(session, onDone, onSaved = null) {
  if (!session) return null;
  return {
    get: session.get,
    put: session.put,
    done: () => {
      if (session.worthSaving) void saveGeo(session.key, session.entries(), onSaved);
      onDone?.(session);
      session.release();
    },
  };
}
